/**
 * Semantic hint extraction for CSS class names.
 *
 * @module playwright-client/semantic-hints
 * @see docs/api/services/browser-session.md
 */

/**
 * Component-framework marker classes that read like human kebab-case but carry
 * no per-element meaning — every control in the framework shares them, so
 * returning one as a "semantic" hint labels unrelated buttons identically
 * (e.g. a whole Telerik toolbar collapsing to `telerik-blazor`). Matched as
 * leading tokens; the `*-` prefixes cover namespaced utility classes
 * (`k-button`, `mat-icon`, `mui-…`, `ant-btn`, …).
 */
function isFrameworkMarkerClass(cls: string): boolean {
  if (cls === 'telerik-blazor') return true;
  // Namespaced component-framework prefixes: Kendo/Telerik (k-), Angular
  // Material (mat-/mdc-), AngularJS (ng-), MUI (mui*), Chakra (chakra-),
  // Ant Design (ant-), Element Plus (el-), Vue/Vuetify (v-), Quasar (q-).
  return /^(k|mat|mdc|ng|chakra|ant|el|v|q)-/.test(cls) || /^mui[A-Z-]/.test(cls);
}

/**
 * Extract a human-readable hint from CSS class names.
 * Filters out framework-generated noise (CSS-in-JS hashes, Tailwind utilities,
 * CSS module hashes, component-framework markers) and returns a semantic
 * class. An action/component keyword anywhere in the class list wins over the
 * generic kebab-case fallback, so a leading framework marker can't shadow a
 * meaningful later class.
 */
export function extractSemanticHint(className: string | undefined): string | undefined {
  if (!className) return undefined;
  const classes = className.split(/\s+/).filter(Boolean);

  const isNoise = (cls: string): boolean => {
    // Framework-generated patterns (CSS-in-JS, emotion, styled-components, svelte)
    if (/^(css|sc|emotion|styled|svelte)-/.test(cls)) return true;
    // Bubble.io framework classes
    if (/^(clickable-element|bubble-element|bubble-r-container|bubble-r-vertical-center)/.test(cls)) return true;
    // CSS module hashes (e.g., _abc123)
    if (/^_[a-zA-Z0-9]{4,}$/.test(cls)) return true;
    // Tailwind utility classes — single-segment prefixed utilities
    if (/^(p|m|w|h|gap|text|bg|border|rounded|shadow|opacity|z|flex|grid|block|inline|hidden|relative|absolute|fixed|sticky|overflow|cursor|transition|ring|outline|sr|not|group|peer|dark|hover|focus|active|disabled|data|aria)-/.test(cls)) return true;
    // Tailwind compound utilities (items-center, justify-between, font-medium, etc.)
    if (/^(items|justify|self|place|space|divide|whitespace|break|font|leading|tracking|decoration|underline|overline|line|list|placeholder|align|content|object|max|min|inset|top|right|bottom|left|order|auto|grow|shrink|basis|table|caption|scale|rotate|translate|skew|origin|select|snap|scroll|touch|resize|appearance|columns|aspect|accent|caret|fill|stroke|will|animate|delay|duration|ease)-./.test(cls)) return true;
    if (/^(flex|grid|block|inline|hidden|table|contents)$/.test(cls)) return true;
    // Component-framework markers (telerik-blazor, k-button, mat-icon, …) —
    // shared across every control, so never element-specific.
    if (isFrameworkMarkerClass(cls)) return true;
    return false;
  };

  const KEYWORD_RE =
    /delete|remove|trash|edit|save|close|submit|cancel|add|create|copy|refresh|back|forward|next|prev|menu|nav|icon|btn|button|action|toggle|collapse|expand|upload|download|export|import|filter|search|settings|help|info|warning|error/i;

  // First pass: prefer a class that names an action/component. Scanning all
  // tokens before the generic kebab-case fallback prevents a leading framework
  // marker from shadowing a meaningful later class (e.g.
  // `telerik-blazor … column-chooser-btn` should resolve to the latter).
  for (const cls of classes) {
    if (isNoise(cls)) continue;
    if (KEYWORD_RE.test(cls)) return cls;
  }
  // Second pass: human-written kebab-case or BEM (2+ segments, 6+ chars).
  // BEM tokens (`block__element`, `block__element--modifier`, e.g. react-select's
  // `select__dropdown-indicator`) are stable, element-specific, and author-written,
  // so they are valid semantic anchors even though they aren't plain kebab-case.
  // Separators allowed between alpha segments: `-` (kebab), `__` (BEM element),
  // `--` (BEM modifier). Hashed/utility noise is already rejected by isNoise.
  for (const cls of classes) {
    if (isNoise(cls)) continue;
    if (/^[a-z][a-z0-9]*((-{1,2}|__)[a-z][a-z0-9]*)+$/.test(cls) && cls.length >= 6) {
      return cls;
    }
  }
  return undefined;
}
